import { memo, useEffect, useMemo } from 'react';
import { Form, FormikProvider } from 'formik';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useCreateInterviewFormController } from 'interviewEditor/useCreateInterviewFormController';
import DraggableSurveyQuestions from 'interviewEditor/CreateSurvey/DraggableSurveyQuestions';
import LoadingPaper from '@components/LoadingPaper';
import InterviewHeader from 'interviewEditor/InterviewHeader';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { breadcrumb } from '@modules/logging';
import { QuestionExpandProvider } from 'interviewEditor/QuestionExpandContext';

const InterviewEditor = () => {
    const { availableInterviews, fetchInterviews } = useInterviewOptions();
    const { selectedInterview, loading } = useInterviewOptions();
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const lightThemeColor = theme.palette.primary.light;

    const initialValues = useMemo(() => {
        if (selectedInterview) {
            return {
                ...selectedInterview,
                puppetAnimation: selectedInterview.puppetAnimation || 'Standing Dog',
                isEditingLocked: selectedInterview.isEditingLocked || false,
            };
        }
        return null;
    }, [selectedInterview]);

    const form = useCreateInterviewFormController(initialValues);

    useEffect(() => {
        breadcrumb('[InterviewEditor] fetching interviews', 'interview editor', {
            availableInterviews,
        });
        fetchInterviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: lightThemeColor,
            }}
        >
            <FormikProvider value={form}>
                <QuestionExpandProvider>
                    <InterviewHeader />
                    <LoadingPaper
                        loading={form.isSubmitting || !availableInterviews || loading}
                        message="Loading survey..."
                        style={{
                            backgroundColor: lightThemeColor,
                            width: '100%',
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            paddingBottom: '10rem',
                        }}
                    >
                        <Form
                            style={{
                                width: isLargeScreen
                                    ? 'max(800px, min(60vw, 1200px))'
                                    : 'max(90vw, min(calc(90vw-250px), 1200px))',
                            }}
                        >
                            <DraggableSurveyQuestions />
                        </Form>
                    </LoadingPaper>
                </QuestionExpandProvider>
            </FormikProvider>
        </div>
    );
};

export default memo(InterviewEditor);
